import React, { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { authService } from "@/services/auth";
import { handleApiError, PASSWORD_REQUIREMENTS_HINT } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEmail?: string;
  onResetComplete?: () => void;
}

type Step = "request" | "verify" | "reset" | "done";

const RESEND_COOLDOWN_SECONDS = 60;
const MIN_PASSWORD_LENGTH = 12;

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) return handleApiError(err);
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
  open,
  onOpenChange,
  initialEmail = "",
  onResetComplete,
}) => {
  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (open) {
      setStep("request");
      setEmail(initialEmail);
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      setError(null);
      setCooldown(0);
    }
  }, [open, initialEmail]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authService.requestPasswordReset(email.trim().toLowerCase());
      setStep("verify");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(errMessage(err, "Could not send a code. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    setLoading(true);
    try {
      await authService.requestPasswordReset(email.trim().toLowerCase());
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(errMessage(err, "Could not resend the code."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authService.verifyResetCode(email.trim().toLowerCase(), code.trim());
      setStep("reset");
    } catch (err: any) {
      setError(errMessage(err, "Invalid or expired code."));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await authService.confirmPasswordReset(
        email.trim().toLowerCase(),
        code.trim(),
        newPassword,
        confirmPassword
      );
      setStep("done");
    } catch (err: any) {
      setError(errMessage(err, "Could not reset your password."));
    } finally {
      setLoading(false);
    }
  };

  const titles: Record<Step, string> = {
    request: "Reset your password",
    verify: "Enter verification code",
    reset: "Set a new password",
    done: "Password updated",
  };

  const descriptions: Record<Step, string> = {
    request: "Enter your email and we'll send you a 6-digit verification code.",
    verify: `We sent a 6-digit code to ${email}. Enter it below to continue.`,
    reset: "Choose a new password for your account.",
    done: "You can now sign in with your new password.",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titles[step]}</DialogTitle>
          <DialogDescription>{descriptions[step]}</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === "request" && (
          <form onSubmit={handleRequest} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fp-email">Email Address</Label>
              <Input
                id="fp-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading || !email}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Code"}
            </Button>
          </form>
        )}

        {step === "verify" && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fp-code">Verification Code</Label>
              <Input
                id="fp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify Code"}
            </Button>
            <div className="text-center text-sm text-muted-foreground">
              <button
                type="button"
                className="underline disabled:no-underline disabled:opacity-60"
                onClick={handleResend}
                disabled={cooldown > 0 || loading}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
            </div>
          </form>
        )}

        {step === "reset" && (
          <form onSubmit={handleReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fp-new">New Password</Label>
              <Input
                id="fp-new"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={MIN_PASSWORD_LENGTH}
              />
              <p className="text-xs text-muted-foreground">{PASSWORD_REQUIREMENTS_HINT}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fp-confirm">Confirm New Password</Label>
              <Input
                id="fp-confirm"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={MIN_PASSWORD_LENGTH}
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
            </Button>
          </form>
        )}

        {step === "done" && (
          <div className="space-y-4">
            <Alert className="border-green-600/50 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100">
              <AlertDescription>
                Your password has been reset. Please sign in with your new password.
              </AlertDescription>
            </Alert>
            <Button
              className="w-full"
              onClick={() => {
                onOpenChange(false);
                onResetComplete?.();
              }}
            >
              Back to Sign In
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
